import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../lib/cart/cartStore.js';
import { useAuth } from '../lib/auth/AuthProvider.jsx';
import { cartApi } from '../lib/cart/cartApi.js';
import { CartLine } from '../components/cart/CartLine.jsx';

export default function CartPage() {
  const nav = useNavigate();
  const { cart, fetchCart, patchItem, removeItem } = useCart();
  const { accessToken, user } = useAuth();

  useEffect(() => { fetchCart(accessToken); }, [accessToken, fetchCart]);

  const inr = (s) => `₹${Number(s).toLocaleString('en-IN')}`;
  const totals = cart?.totals;
  const groups = cart?.lines_by_brand ?? [];
  const empty = groups.every((g) => g.lines.length === 0);

  async function startCheckout() {
    if (!user || !accessToken) {
      alert('Please sign in on any brand site first, then return here.');
      return;
    }
    try {
      const res = await cartApi.checkout(
        {
          customer: { name: user.name, phone: user.phone, email: user.email },
          channel: 'superhomes_web',
          return_to_url: `${window.location.origin}/order-confirmation`,
        },
        accessToken,
      );
      nav(`/checkout?session=${res.checkout_session_id}`);
    } catch (e) {
      alert(e?.detail?.errors?.[0]?.message || 'Could not start checkout.');
    }
  }

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-10 py-8 min-h-[70vh]">
      <h1 className="text-3xl font-bold tracking-tight mb-6">Your cart</h1>
      {empty ? (
        <div className="text-center py-16">
          <p className="text-slate-500 mb-4">Your cart is empty.</p>
          <Link to="/" className="text-superhomes-500 font-semibold">← Continue browsing</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
          <div>
            {groups.map((g) => (
              <section key={g.brand.id} className="mb-8">
                <div className="text-xs font-bold tracking-widest uppercase text-slate-400 mb-2">{g.brand.name}</div>
                {g.lines.map((line) => (
                  <CartLine
                    key={line.id} line={line}
                    onQtyChange={(next) => patchItem(line.id, { qty: next }, accessToken)}
                    onRemove={() => removeItem(line.id, accessToken)}
                  />
                ))}
              </section>
            ))}
          </div>
          <aside className="lg:sticky lg:top-6 self-start bg-white/70 backdrop-blur border border-slate-200 rounded-2xl p-6">
            <h3 className="text-base font-bold mb-4">Order summary</h3>
            {totals && (
              <>
                <Row label="Subtotal" value={inr(totals.subtotal)} />
                <div className="border-t border-slate-100 my-3" />
                <Row label="Due now" value={inr(totals.due_now)} bold />
                {Number(totals.balance_later) > 0 && (
                  <Row label="Balance later" value={inr(totals.balance_later)} muted />
                )}
              </>
            )}
            <button onClick={startCheckout}
              className="mt-5 w-full bg-superhomes-500 hover:bg-superhomes-600 text-white rounded-xl py-4 font-bold">
              {user ? `Proceed to pay ${totals ? inr(totals.due_now) : ''}` : 'Sign in to checkout'}
            </button>
            <p className="text-xs text-slate-400 text-center mt-3">🔒 Secure payment · GST included</p>
          </aside>
        </div>
      )}
    </main>
  );
}

function Row({ label, value, bold, muted }) {
  return (
    <div className={`flex justify-between py-1 ${bold ? 'text-base font-bold' : 'text-sm'} ${muted ? 'text-slate-400' : 'text-slate-800'}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}
